<template>
  <div class="catalog-page">
    <!-- Barra de anuncio -->
    <div class="catalog-announcement" v-if="sellerName">
      Envíos a todo el país · Atención personalizada por {{ sellerName }}
    </div>

    <!-- Header -->
    <header class="catalog-header">
      <div class="catalog-header-inner">
        <div class="catalog-brand" @click="loadProducts(1)">
          <img v-if="logoUrl" :src="logoUrl" alt="Logo" class="catalog-logo" />
          <span v-else class="catalog-brand-name">Catálogo Mayorista</span>
        </div>

        <div class="catalog-search-wrapper">
          <i class="fas fa-search catalog-search-icon"></i>
          <input
            v-model="search"
            @input="onSearch"
            type="text"
            class="catalog-search"
            placeholder="Buscar"
          />
          <button v-if="search" class="catalog-search-clear" @click="clearSearch" aria-label="Limpiar búsqueda">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <button class="catalog-header-cart" @click="showCheckout = true" aria-label="Ver carrito">
          <i class="fas fa-shopping-bag"></i>
          <span v-if="cartCount > 0" class="catalog-header-cart-badge">{{ cartCount }}</span>
        </button>
      </div>
    </header>

    <!-- Productos -->
    <main class="catalog-main">
      <div class="catalog-toolbar">
        <h1 class="catalog-heading">Productos</h1>
        <p class="catalog-count" v-if="!loadingProducts && pagination.total > 0">
          {{ pagination.total }} {{ pagination.total === 1 ? 'producto' : 'productos' }}
        </p>
      </div>

      <!-- Skeletons de carga -->
      <div v-if="loadingProducts" class="catalog-grid">
        <div v-for="n in 8" :key="'sk-' + n" class="catalog-card catalog-skeleton">
          <div class="catalog-skeleton-img"></div>
          <div class="catalog-skeleton-line"></div>
          <div class="catalog-skeleton-line catalog-skeleton-line--short"></div>
        </div>
      </div>

      <div v-else-if="products.length === 0" class="catalog-empty">
        <i class="fas fa-search catalog-empty-icon"></i>
        <p>No se encontraron productos.</p>
      </div>

      <div v-else class="catalog-grid">
        <div v-for="p in products" :key="p.id" class="catalog-card">
          <div class="catalog-card-img" @click="addToCart(p)">
            <img v-if="p.image_url && !p.imgError" :src="p.image_url" :alt="p.name" @error="p.imgError = true" />
            <i v-else class="fas fa-shoe-prints catalog-img-placeholder"></i>
            <button class="catalog-quick-add" @click.stop="addToCart(p)">
              <i class="fas" :class="p.has_variants ? 'fa-sliders-h' : 'fa-plus'"></i>
              {{ p.has_variants ? 'Elegir opciones' : 'Agregar' }}
            </button>
          </div>
          <div class="catalog-card-info" @click="addToCart(p)">
            <h3 class="catalog-card-name">{{ p.name }}</h3>
            <p class="catalog-card-ref" v-if="p.ref">{{ p.ref }}</p>
            <p class="catalog-card-price">{{ priceLabel(p) }}</p>
          </div>
        </div>
      </div>

      <!-- Cargando más productos (scroll infinito) -->
      <div v-if="loadingMore" class="catalog-loading-more">
        <i class="fas fa-spinner fa-spin"></i> Cargando más productos...
      </div>
      <div v-else-if="!loadingProducts && products.length > 0 && pagination.currentPage >= pagination.lastPage" class="catalog-end-list">
        No hay más productos
      </div>
    </main>

    <!-- Footer -->
    <footer class="catalog-footer">
      <span v-if="sellerName">{{ sellerName }}</span>
      <span class="catalog-footer-dot" v-if="sellerName">·</span>
      <span>Catálogo powered by MiStock</span>
    </footer>

    <!-- Modal de selección de variante -->
    <div v-if="showVariantModal" class="catalog-modal-overlay" @click.self="closeVariantModal">
      <div class="catalog-modal" role="dialog" aria-modal="true">
        <button class="catalog-modal-x" @click="closeVariantModal" aria-label="Cerrar">
          <i class="fas fa-times"></i>
        </button>

        <div class="catalog-modal-header">
          <div class="catalog-modal-thumb">
            <img v-if="pendingProduct && pendingProduct.image_url && !pendingProduct.imgError"
                 :src="pendingProduct.image_url" :alt="pendingProduct.name" />
            <i v-else class="fas fa-shoe-prints"></i>
          </div>
          <div class="catalog-modal-header-info">
            <h3>{{ pendingProduct?.name }}</h3>
            <p class="catalog-modal-price">
              {{ selectedVariants.length === 1
                ? `$${formatPrice(selectedVariants[0].price)}`
                : (selectedVariants.length > 1 ? `Total: $${formatPrice(selectedVariantsTotal)}` : priceLabel(pendingProduct || {})) }}
            </p>
          </div>
        </div>

        <p class="catalog-modal-label">Selecciona una o varias opciones</p>

        <div class="catalog-variant-list">
          <button
            v-for="v in pendingProduct?.variants"
            :key="v.id"
            class="catalog-variant-row"
            :class="{ 'is-selected': selectedVariantIds.includes(v.id) }"
            @click="toggleVariant(v.id)"
            type="button"
          >
            <span class="catalog-variant-check" aria-hidden="true"></span>
            <span class="catalog-variant-title">{{ v.title }}</span>
            <span v-if="v.inventory_quantity > 0 && v.inventory_quantity <= 3" class="catalog-variant-stock">
              Últimas {{ v.inventory_quantity }}
            </span>
            <span class="catalog-variant-price">${{ formatPrice(v.price) }}</span>
          </button>
        </div>

        <button class="catalog-modal-add" :disabled="selectedVariants.length === 0" @click="confirmVariants">
          <i class="fas fa-shopping-bag"></i>
          {{ selectedVariants.length > 1 ? `Agregar ${selectedVariants.length} al carrito` : 'Agregar al carrito' }}
        </button>
      </div>
    </div>

    <!-- Aviso de agregado -->
    <transition name="toast">
      <div v-if="toast.visible" class="catalog-toast">
        <i class="fas fa-check-circle"></i> {{ toast.text }}
      </div>
    </transition>

    <!-- Carrito flotante (móvil) -->
    <div class="catalog-cart-fab" @click="showCheckout = true">
      <i class="fas fa-shopping-cart"></i>
      <span class="catalog-cart-fab-label">Carrito</span>
      <span v-if="cartCount > 0" class="catalog-cart-badge">{{ cartCount }}</span>
    </div>

    <!-- Checkout lateral -->
    <transition name="slide">
      <div v-if="showCheckout" class="catalog-checkout-overlay" @click.self="showCheckout = false">
        <div class="catalog-checkout-panel">
          <div class="catalog-checkout-header">
            <h2>Resumen del pedido</h2>
            <button class="catalog-checkout-close" @click="showCheckout = false"><i class="fas fa-times"></i></button>
          </div>

          <div class="catalog-checkout-items" v-if="cart.length > 0">
            <div v-for="item in cart" :key="item.key" class="catalog-checkout-item">
              <div class="catalog-checkout-item-info">
                <h4>{{ item.name }}</h4>
                <p v-if="item.variantTitle">{{ item.variantTitle }}</p>
                <p class="catalog-checkout-item-price">${{ formatPrice(item.price) }} c/u</p>
              </div>
              <div class="catalog-checkout-item-qty">
                <button @click="changeQty(item, -1)">−</button>
                <span>{{ item.qty }}</span>
                <button @click="changeQty(item, 1)">+</button>
              </div>
              <div class="catalog-checkout-item-total">
                ${{ formatPrice(item.price * item.qty) }}
              </div>
              <button class="catalog-checkout-item-remove" @click="removeItem(item)" aria-label="Quitar">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
          <div v-else class="catalog-checkout-empty">
            <i class="fas fa-shopping-cart fa-3x"></i>
            <p>Tu carrito está vacío</p>
          </div>

          <div class="catalog-checkout-total" v-if="cart.length > 0">
            <span>Total:</span>
            <span class="catalog-checkout-total-value">${{ formatPrice(cartTotal) }}</span>
          </div>

          <!-- Formulario de cliente -->
          <div class="catalog-checkout-form" v-if="cart.length > 0">
            <h3>Datos de contacto</h3>
            <input v-model="form.name" type="text" placeholder="Nombre completo *" />
            <input v-model="form.document" type="text" placeholder="Cédula *" />
            <input v-model="form.address" type="text" placeholder="Dirección *" />
            <div class="catalog-checkout-row">
              <input v-model="form.department" type="text" placeholder="Departamento *" />
              <input v-model="form.municipality" type="text" placeholder="Municipio *" />
            </div>

            <button class="catalog-checkout-submit" @click="sendOrder" :disabled="sending">
              <i v-if="sending" class="fas fa-spinner fa-spin"></i>
              <template v-else><i class="fab fa-whatsapp"></i> Enviar pedido por WhatsApp</template>
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import catalogService from '@/services/catalog.service';

export default {
  name: 'PublicCatalog',
  data() {
    return {
      token: '',
      sellerName: '',
      sellerPhone: '',
      logoUrl: '',
      products: [],
      loadingProducts: true,
      loadingMore: false,
      search: '',
      searchTimer: null,
      pagination: { currentPage: 1, lastPage: 1, total: 0 },
      cart: [],
      showCheckout: false,
      showVariantModal: false,
      pendingProduct: null,
      selectedVariantIds: [],
      sending: false,
      toast: { visible: false, text: '' },
      toastTimer: null,
      form: {
        name: '',
        document: '',
        address: '',
        department: '',
        municipality: '',
      },
    };
  },
  computed: {
    cartCount() {
      return this.cart.reduce((s, i) => s + i.qty, 0);
    },
    cartTotal() {
      return this.cart.reduce((s, i) => s + i.price * i.qty, 0);
    },
    selectedVariants() {
      if (!this.pendingProduct) return [];
      return this.pendingProduct.variants.filter(v => this.selectedVariantIds.includes(v.id));
    },
    selectedVariantsTotal() {
      return this.selectedVariants.reduce((s, v) => s + parseFloat(v.price || 0), 0);
    },
  },
  methods: {
    formatPrice(val) {
      return parseFloat(val || 0).toLocaleString('es-CO', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    // Etiqueta de precio estilo Shopify: "Desde $X" cuando las variantes tienen precios distintos
    priceLabel(p) {
      if (p && p.has_variants && p.variants && p.variants.length) {
        const prices = p.variants.map(v => parseFloat(v.price)).filter(n => !isNaN(n));
        if (prices.length) {
          const min = Math.min(...prices);
          if (prices.some(pr => pr !== min)) return `Desde $${this.formatPrice(min)}`;
        }
      }
      return `$${this.formatPrice(p ? p.sale_price : 0)}`;
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.loadProducts(1), 400);
    },
    clearSearch() {
      this.search = '';
      this.loadProducts(1);
    },
    async loadProducts(page = 1) {
      // El scroll infinito agrega páginas a la lista; solo la primera página (o nueva búsqueda) reemplaza los productos
      const append = page > 1;
      if (append) {
        this.loadingMore = true;
      } else {
        this.loadingProducts = true;
        window.scrollTo({ top: 0 });
      }
      try {
        const params = { page, per_page: 12 };
        if (this.search) params.search = this.search;
        const res = await catalogService.getProducts(params);
        const d = res.data;
        const nuevos = (d.data || []).map(p => ({ ...p, imgError: false }));
        this.products = append ? [...this.products, ...nuevos] : nuevos;
        this.pagination = {
          currentPage: d.current_page || 1,
          lastPage: d.last_page || 1,
          total: d.total || 0,
        };
      } catch (e) {
        console.error('Error loading products:', e);
      } finally {
        this.loadingProducts = false;
        this.loadingMore = false;
        // Si el contenido no llena la pantalla, sigue cargando hasta llenarla o llegar a la última página
        this.$nextTick(() => this.onScroll());
      }
    },
    loadMoreProducts() {
      if (this.loadingProducts || this.loadingMore) return;
      if (this.pagination.currentPage >= this.pagination.lastPage) return;
      this.loadProducts(this.pagination.currentPage + 1);
    },
    onScroll() {
      const umbral = 600; // px antes del final de la página
      const restante = document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
      if (restante <= umbral) this.loadMoreProducts();
    },
    async loadSellerInfo() {
      try {
        const res = await catalogService.getSellerInfo(this.token);
        this.sellerName = res.data.seller_name || '';
        this.sellerPhone = res.data.seller_phone || '';
        const logoPath = res.data.logo_path;
        if (logoPath) {
          const base = process.env.VUE_APP_API_IMAGE_URL || '';
          this.logoUrl = base + logoPath;
        }
      } catch (e) {
        console.error('Error loading seller info:', e);
      }
    },
    addToCart(product) {
      if (product.has_variants && product.variants && product.variants.length > 0) {
        this.pendingProduct = product;
        this.selectedVariantIds = [];
        this.showVariantModal = true;
        return;
      }
      this.pushToCart(product, null);
      this.showToast('Producto agregado al carrito');
    },
    closeVariantModal() {
      this.showVariantModal = false;
      this.pendingProduct = null;
      this.selectedVariantIds = [];
    },
    toggleVariant(id) {
      if (this.selectedVariantIds.includes(id)) {
        this.selectedVariantIds = this.selectedVariantIds.filter(vid => vid !== id);
      } else {
        this.selectedVariantIds = [...this.selectedVariantIds, id];
      }
    },
    confirmVariants() {
      if (!this.selectedVariants.length) return;
      this.selectedVariants.forEach(v => this.pushToCart(this.pendingProduct, v));
      const cantidad = this.selectedVariants.length;
      this.closeVariantModal();
      this.showToast(cantidad === 1 ? 'Producto agregado al carrito' : `${cantidad} productos agregados al carrito`);
    },
    pushToCart(product, variant) {
      const key = variant ? `${product.id}_${variant.id}` : `${product.id}`;
      const existing = this.cart.find(i => i.key === key);
      if (existing) {
        existing.qty++;
      } else {
        this.cart.push({
          key,
          productId: product.id,
          name: product.name,
          variantId: variant ? variant.id : null,
          variantTitle: variant ? variant.title : '',
          price: variant ? parseFloat(variant.price) : parseFloat(product.sale_price),
          qty: 1,
        });
      }
    },
    showToast(text) {
      clearTimeout(this.toastTimer);
      this.toast = { visible: true, text };
      this.toastTimer = setTimeout(() => {
        this.toast.visible = false;
      }, 2200);
    },
    changeQty(item, delta) {
      item.qty += delta;
      if (item.qty <= 0) {
        this.cart = this.cart.filter(i => i.key !== item.key);
      }
    },
    removeItem(item) {
      this.cart = this.cart.filter(i => i.key !== item.key);
    },
    sendOrder() {
      if (!this.form.name || !this.form.address) {
        alert('Por favor completa al menos nombre y dirección.');
        return;
      }
      this.sending = true;

      const line = '─'.repeat(28);
      let msg = '';
      msg += `*PEDIDO DE CATÁLOGO*\n`;
      msg += `${line}\n`;
      msg += `Vendedor: ${this.sellerName || 'N/A'}\n`;
      msg += `Fecha: ${new Date().toLocaleString('es-CO')}\n`;
      msg += `${line}\n\n`;
      msg += `*PRODUCTOS*\n`;
      msg += `${line}\n`;
      this.cart.forEach((item, idx) => {
        msg += `${idx + 1}. ${item.name}\n`;
        if (item.variantTitle) msg += `   Variante: ${item.variantTitle}\n`;
        msg += `   Cant: ${item.qty} x $${this.formatPrice(item.price)}\n`;
        msg += `   Subtotal: $${this.formatPrice(item.price * item.qty)}\n`;
        if (idx < this.cart.length - 1) msg += `\n`;
      });
      msg += `${line}\n`;
      msg += `*TOTAL: $${this.formatPrice(this.cartTotal)}*\n`;
      msg += `${line}\n\n`;
      msg += `*DATOS DEL CLIENTE*\n`;
      msg += `${line}\n`;
      msg += `Nombre: ${this.form.name}\n`;
      if (this.form.document) msg += `Cédula: ${this.form.document}\n`;
      msg += `Dirección: ${this.form.address}\n`;
      if (this.form.department) msg += `Departamento: ${this.form.department}\n`;
      if (this.form.municipality) msg += `Municipio: ${this.form.municipality}\n`;
      msg += `${line}\n`;
      msg += `¡Gracias! 🛍️`;

      const phone = this.sellerPhone ? this.sellerPhone.replace(/\D/g, '') : '';
      const waNumber = phone.startsWith('57') ? phone : '57' + phone;
      const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;

      window.open(url, '_blank');
      this.sending = false;
    },
  },
  mounted() {
    this.token = this.$route.params.token;
    if (this.token) {
      this.loadSellerInfo();
    }
    this.loadProducts();
    window.addEventListener('keydown', this.onKeydown);
    window.addEventListener('scroll', this.onScroll, { passive: true });
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown);
    window.removeEventListener('scroll', this.onScroll);
  },
};
</script>

<style scoped>
.catalog-page {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, Helvetica, sans-serif;
  background: #ffffff;
  color: #121212;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Barra de anuncio */
.catalog-announcement {
  background: #121212;
  color: #fff;
  text-align: center;
  font-size: 12px;
  letter-spacing: 0.08em;
  padding: 9px 16px;
}

/* Header */
.catalog-header {
  background: #fff;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  z-index: 100;
}
.catalog-header-inner {
  max-width: 1500px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
}
.catalog-brand {
  display: flex;
  align-items: center;
  cursor: pointer;
  flex-shrink: 0;
}
.catalog-logo {
  max-height: 48px;
  max-width: 180px;
  object-fit: contain;
}
.catalog-brand-name {
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.catalog-search-wrapper {
  position: relative;
  width: 100%;
  max-width: 460px;
}
.catalog-search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 14px;
  pointer-events: none;
}
.catalog-search {
  width: 100%;
  padding: 11px 40px 11px 38px;
  border: 1px solid #ccc;
  border-radius: 0;
  background: #fff;
  font-size: 14px;
  outline: none;
  transition: border-color 0.15s;
}
.catalog-search:focus {
  border-color: #121212;
}
.catalog-search-clear {
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: none;
  color: #888;
  cursor: pointer;
  padding: 6px;
  font-size: 13px;
}
.catalog-search-clear:hover {
  color: #121212;
}
.catalog-header-cart {
  position: relative;
  border: none;
  background: none;
  font-size: 19px;
  color: #121212;
  cursor: pointer;
  padding: 8px 10px;
  flex-shrink: 0;
  transition: opacity 0.15s;
}
.catalog-header-cart:hover {
  opacity: 0.7;
}
.catalog-header-cart-badge {
  position: absolute;
  top: 0;
  right: 0;
  background: #121212;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  min-width: 17px;
  height: 17px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* Productos */
.catalog-main {
  max-width: 1500px;
  width: 100%;
  margin: 0 auto;
  padding: 32px 24px 64px;
  flex: 1;
}
.catalog-toolbar {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 28px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}
.catalog-heading {
  font-size: 24px;
  font-weight: 400;
  letter-spacing: 0.02em;
  margin: 0;
  color: #121212;
}
.catalog-count {
  font-size: 13px;
  color: #888;
  margin: 0;
}
.catalog-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px 16px;
}
.catalog-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
}
.catalog-card-img {
  position: relative;
  width: 100%;
  aspect-ratio: 3 / 4;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f6f6f6;
  overflow: hidden;
}
.catalog-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}
.catalog-card:hover .catalog-card-img img {
  transform: scale(1.05);
}
.catalog-img-placeholder {
  font-size: 48px;
  color: #ccc;
}
.catalog-quick-add {
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 12px;
  padding: 11px;
  border: 1px solid #121212;
  background: rgba(255, 255, 255, 0.95);
  color: #121212;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 0.2s ease, transform 0.2s ease, background 0.15s, color 0.15s;
}
.catalog-card:hover .catalog-quick-add,
.catalog-quick-add:focus-visible {
  opacity: 1;
  transform: translateY(0);
}
.catalog-quick-add:hover {
  background: #121212;
  color: #fff;
}
.catalog-card-info {
  padding: 14px 2px 0;
}
.catalog-card-name {
  font-size: 14px;
  font-weight: 400;
  color: #121212;
  margin: 0 0 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.catalog-card:hover .catalog-card-name {
  text-decoration: underline;
  text-underline-offset: 3px;
}
.catalog-card-ref {
  font-size: 12px;
  color: #999;
  margin: 0 0 4px;
}
.catalog-card-price {
  font-size: 15px;
  font-weight: 500;
  color: #121212;
  margin: 0;
}

/* Skeletons de carga */
.catalog-skeleton {
  cursor: default;
  pointer-events: none;
}
.catalog-skeleton-img {
  width: 100%;
  aspect-ratio: 3 / 4;
  background: linear-gradient(100deg, #f2f2f2 40%, #fafafa 50%, #f2f2f2 60%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}
.catalog-skeleton-line {
  height: 12px;
  margin-top: 14px;
  width: 70%;
  background: linear-gradient(100deg, #f2f2f2 40%, #fafafa 50%, #f2f2f2 60%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.4s infinite;
}
.catalog-skeleton-line--short {
  width: 40%;
  margin-top: 10px;
}
@keyframes skeleton-shimmer {
  0% { background-position: 120% 0; }
  100% { background-position: -80% 0; }
}

/* Loading / empty */
.catalog-empty {
  text-align: center;
  padding: 80px 20px;
  color: #999;
}
.catalog-empty p {
  margin-top: 12px;
  font-size: 14px;
}
.catalog-empty-icon {
  font-size: 40px;
  color: #ddd;
}

/* Scroll infinito */
.catalog-loading-more {
  text-align: center;
  padding: 40px 0 8px;
  font-size: 13px;
  color: #666;
  letter-spacing: 0.04em;
}
.catalog-end-list {
  text-align: center;
  padding: 40px 0 8px;
  font-size: 12px;
  color: #aaa;
  letter-spacing: 0.04em;
}

/* Footer */
.catalog-footer {
  border-top: 1px solid #e8e8e8;
  padding: 20px 24px;
  text-align: center;
  font-size: 12px;
  color: #888;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}
.catalog-footer-dot {
  color: #ccc;
}

/* Modal variante */
.catalog-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.catalog-modal {
  position: relative;
  background: #fff;
  border-radius: 0;
  padding: 24px;
  width: 100%;
  max-width: 460px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}
.catalog-modal-x {
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  background: none;
  font-size: 17px;
  color: #666;
  cursor: pointer;
  padding: 6px;
  transition: color 0.15s;
}
.catalog-modal-x:hover {
  color: #121212;
}
.catalog-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  flex-shrink: 0;
  padding-right: 28px;
}
.catalog-modal-thumb {
  width: 64px;
  height: 80px;
  flex-shrink: 0;
  background: #f6f6f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.catalog-modal-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.catalog-modal-thumb i {
  font-size: 22px;
  color: #ccc;
}
.catalog-modal-header-info h3 {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
}
.catalog-modal-price {
  margin: 0;
  font-size: 15px;
  font-weight: 500;
}
.catalog-modal-label {
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #888;
  margin: 0 0 10px;
  flex-shrink: 0;
}
.catalog-variant-list {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}
.catalog-variant-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 13px 14px;
  border: 1px solid #e0e0e0;
  background: #fff;
  margin-bottom: 8px;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.15s, background 0.15s;
}
.catalog-variant-row:hover:not(:disabled) {
  border-color: #121212;
}
.catalog-variant-row.is-selected {
  border-color: #121212;
  background: #f7f7f7;
}
.catalog-variant-check {
  width: 16px;
  height: 16px;
  border: 1px solid #999;
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.15s, background 0.15s;
}
.catalog-variant-row.is-selected .catalog-variant-check {
  border-color: #121212;
  background: #121212;
}
.catalog-variant-row.is-selected .catalog-variant-check::after {
  content: '';
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.catalog-variant-title {
  flex: 1;
}
.catalog-variant-stock {
  font-size: 11px;
  color: #b45309;
  white-space: nowrap;
}
.catalog-variant-price {
  font-weight: 600;
  white-space: nowrap;
}
.catalog-modal-add {
  width: 100%;
  padding: 14px;
  border: 1px solid #121212;
  background: #121212;
  color: #fff;
  margin-top: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.04em;
  font-family: inherit;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.catalog-modal-add:hover:not(:disabled) {
  opacity: 0.85;
}
.catalog-modal-add:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Toast de agregado */
.catalog-toast {
  position: fixed;
  bottom: 92px;
  right: 24px;
  background: #121212;
  color: #fff;
  font-size: 13px;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  z-index: 400;
}
.toast-enter-active,
.toast-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Carrito FAB */
.catalog-cart-fab {
  position: fixed;
  bottom: 24px;
  right: 24px;
  height: 52px;
  min-width: 52px;
  padding: 0 18px;
  border-radius: 28px;
  background: #121212;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 17px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  cursor: pointer;
  z-index: 150;
  transition: transform 0.15s ease;
}
.catalog-cart-fab:hover {
  transform: translateY(-2px);
}
.catalog-cart-fab-label {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.catalog-cart-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #48bb78;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* Checkout */
.catalog-checkout-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 300;
}
.catalog-checkout-panel {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: 440px;
  background: #fff;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}
.catalog-checkout-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 24px;
  border-bottom: 1px solid #e8e8e8;
  position: sticky;
  top: 0;
  background: #fff;
  z-index: 1;
}
.catalog-checkout-header h2 {
  font-size: 17px;
  font-weight: 600;
  margin: 0;
}
.catalog-checkout-close {
  border: none;
  background: none;
  font-size: 18px;
  cursor: pointer;
  color: #666;
  padding: 4px;
}
.catalog-checkout-close:hover {
  color: #121212;
}
.catalog-checkout-items {
  padding: 8px 24px;
  flex: 1;
}
.catalog-checkout-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f0f0;
}
.catalog-checkout-item-info {
  flex: 1;
}
.catalog-checkout-item-info h4 {
  font-size: 14px;
  font-weight: 500;
  margin: 0;
}
.catalog-checkout-item-info p {
  font-size: 12px;
  color: #999;
  margin: 0;
}
.catalog-checkout-item-price {
  font-size: 11px !important;
}
.catalog-checkout-item-qty {
  display: flex;
  align-items: center;
  gap: 8px;
}
.catalog-checkout-item-qty button {
  width: 28px;
  height: 28px;
  border: 1px solid #ddd;
  border-radius: 0;
  background: #fff;
  cursor: pointer;
  font-size: 15px;
  transition: border-color 0.15s;
}
.catalog-checkout-item-qty button:hover {
  border-color: #121212;
}
.catalog-checkout-item-qty span {
  font-weight: 600;
  min-width: 20px;
  text-align: center;
  font-size: 14px;
}
.catalog-checkout-item-total {
  font-weight: 600;
  font-size: 14px;
  min-width: 70px;
  text-align: right;
}
.catalog-checkout-item-remove {
  border: none;
  background: none;
  color: #bbb;
  cursor: pointer;
  font-size: 13px;
  transition: color 0.15s;
}
.catalog-checkout-item-remove:hover {
  color: #e53e3e;
}
.catalog-checkout-empty {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}
.catalog-checkout-total {
  padding: 18px 24px;
  border-top: 1px solid #e8e8e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 17px;
  font-weight: 600;
}
.catalog-checkout-total-value {
  color: #121212;
}

/* Form */
.catalog-checkout-form {
  padding: 16px 24px 24px;
  border-top: 1px solid #e8e8e8;
}
.catalog-checkout-form h3 {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 12px;
}
.catalog-checkout-form input,
.catalog-checkout-form textarea {
  width: 100%;
  padding: 11px 14px;
  border: 1px solid #ddd;
  border-radius: 0;
  font-size: 14px;
  font-family: inherit;
  margin-bottom: 10px;
  outline: none;
  transition: border-color 0.15s;
  resize: vertical;
}
.catalog-checkout-form input:focus,
.catalog-checkout-form textarea:focus {
  border-color: #121212;
}
.catalog-checkout-row {
  display: flex;
  gap: 10px;
}
.catalog-checkout-row input {
  flex: 1;
  min-width: 0;
}
.catalog-checkout-submit {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 0;
  background: #25d366;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: opacity 0.15s;
}
.catalog-checkout-submit:hover:not(:disabled) {
  opacity: 0.9;
}
.catalog-checkout-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Slide transition */
.slide-enter-active,
.slide-leave-active {
  transition: opacity 0.3s;
}
.slide-enter-active .catalog-checkout-panel,
.slide-leave-active .catalog-checkout-panel {
  transition: transform 0.3s;
}
.slide-enter-from .catalog-checkout-panel,
.slide-leave-to .catalog-checkout-panel {
  transform: translateX(100%);
}

/* Tablet */
@media (min-width: 750px) {
  .catalog-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 36px 20px;
  }
  .catalog-header-inner {
    padding: 16px 40px;
  }
  .catalog-main {
    padding: 40px 40px 72px;
  }
  .catalog-cart-fab {
    display: none;
  }
  .catalog-toast {
    bottom: 32px;
  }
}

/* Escritorio */
@media (min-width: 990px) {
  .catalog-grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 48px 24px;
  }
  .catalog-heading {
    font-size: 28px;
  }
  .catalog-header-inner {
    padding: 18px 48px;
  }
  .catalog-main {
    padding: 48px 48px 80px;
  }
  .catalog-quick-add {
    left: 16px;
    right: 16px;
    bottom: 16px;
  }
}

/* Móvil pequeño: header compacto */
@media (max-width: 749px) {
  .catalog-header-cart {
    display: none;
  }
}
@media (max-width: 599px) {
  .catalog-header-inner {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
    padding: 12px 16px;
  }
  .catalog-brand {
    justify-content: center;
  }
  .catalog-quick-add {
    display: none;
  }
  .catalog-card:active {
    opacity: 0.85;
  }
  .catalog-toolbar {
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
  }
  .catalog-cart-fab-label {
    display: none;
  }
  .catalog-cart-fab {
    width: 52px;
    padding: 0;
    justify-content: center;
  }
}
</style>
